import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { PointOptionsObject } from 'highcharts';
import { sort } from 'remeda';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { formatDateStandard } from 'src/app/shared/standard-date.pipe';
import { AutoDispatchStats, AutoDispatchStatsGraphData, DriverShiftOverviewResponse } from 'src/app/types';
import { AutoDispatchStatsService, StatsColumnNames, StatsRequestData } from '../auto-dispatch-stats.service';
import { ShiftService } from '../shift.service';

@Component({
  selector: 'lohi-auto-dispatch-stats',
  templateUrl: './auto-dispatch-stats.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoDispatchStatsComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public statsTable$: Observable<AutoDispatchStats[]>;
  public statsGraph$: Observable<AutoDispatchStatsGraphData[]>;
  public regions$: Observable<DriverShiftOverviewResponse>;
  public statsRequestData$: Observable<StatsRequestData>;
  public chart: Chart;
  constructor(private statsService: AutoDispatchStatsService, private shiftService: ShiftService) {}

  public ngOnInit(): void {
    this.statsRequestData$ = this.statsService.statsRequestData$;
    this.statsTable$ = this.statsService.statsTable$.pipe(
      map((stats) => sort(stats || [], (a, b) => a.demandSupplyRatio - b.demandSupplyRatio)),
    );
    this.statsGraph$ = this.statsService.statsGraph$;
    this.regions$ = this.shiftService.driversOnShift$;
    this.chart = new Chart({
      chart: {
        type: 'spline',
      },
      title: null,
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {},
      },
      tooltip: {
        formatter() {
          return `Supply/Demand: <span class="font-bold">${
            this.point.options.custom.demandSupplyRatio
          }<span><br>${formatDateStandard(this.x)}: ${this.y}`;
          // return formatDateStandard(this.x);
        },
      },
      xAxis: {
        type: 'datetime',
        labels: {
          formatter() {
            return formatDateStandard(this.value);
          },
        },
      },
    });
    this.chart.ref$.pipe(take(1)).subscribe((chart) => {
      this.listenToGraphData(chart);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public setRegion(regionId: number) {
    this.statsService.setRegion(regionId);
  }

  public selectColumn(columnName: StatsColumnNames) {
    this.statsService.setColumn(columnName);
  }

  public setDaysOfHistory(daysOfHistory: number) {
    this.statsService.setDaysOfHistory(daysOfHistory);
  }

  private listenToGraphData(chart: Highcharts.Chart) {
    this.statsGraph$.pipe(takeUntil(this.destroy$$)).subscribe((data) => {
      if (!data || Object.keys(data).length === 0) {
        return;
      }

      const colorFn = colorFunction(
        '#448AFF',
        '#EF6048',
        data.map((d) => d.demandSupplyRatio),
      );

      const seriesData: Array<PointOptionsObject> = [];
      data.forEach((datum) => {
        seriesData.push({
          y: datum.value,
          x: new Date(datum.createdAt).getTime(),
          color: colorFn(datum.demandSupplyRatio),
          custom: {
            demandSupplyRatio: datum.demandSupplyRatio.toFixed(2),
          },
        });
      });
      if (chart.series.length) {
        chart.series[0].setData(seriesData);
      } else {
        chart.addSeries(
          {
            type: 'scatter',
            data: seriesData,
          },
          false,
          false,
        );
      }
      chart.redraw();
    });
  }
}

// adapated from https://stackoverflow.com/questions/3080421/javascript-color-gradient
function hexToRgb(hex): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function colorFunction(startColor: string, endColor: string, values: number[]): (value: number) => string {
  const { min, max } = getMinMax(values);
  if (min === max) {
    return (value: number) => startColor;
  }
  const startRGB = hexToRgb(startColor);
  const endRGB = hexToRgb(endColor);
  const diffRed = endRGB.r - startRGB.r;
  const diffGreen = endRGB.g - startRGB.g;
  const diffBlue = endRGB.b - startRGB.b;

  return (value: number) => {
    const percent = (value - min) / (max - min);
    const valueDiffRed = diffRed * percent + startRGB.r;
    const valueDiffGreen = diffGreen * percent + startRGB.g;
    const valueDiffBlue = diffBlue * percent + startRGB.b;
    return `rgb(${Math.round(valueDiffRed)},${Math.round(valueDiffGreen)},${Math.round(valueDiffBlue)})`;
  };
}

function getMinMax(values: number[]): { min: number; max: number } {
  let min: number;
  let max: number;
  if (values.length === 0) {
    return { min: null, max: null };
  }
  values.forEach((value) => {
    if (min === undefined || value < min) {
      min = value;
    }
    if (max === undefined || value > max) {
      max = value;
    }
  });
  return {
    min,
    max,
  };
}
